import { defineStore } from "pinia";
import api from "../api";
import { onServerEvent } from "../eventStream";
import i18n from "../i18n";
import type { FeedItem, FeedPage } from "../types/notifications";
import { useAuthStore } from "./auth";

// Вне state: подписка на SSE делается один раз за жизнь модуля.
let subscribed = false;

export const useNotificationsStore = defineStore("notifications", {
  state: () => ({
    unreadCount: 0,
    items: [] as FeedItem[],
    nextCursor: null as string | null,
    loading: false,
  }),
  actions: {
    async fetchCount() {
      try {
        const res = await api.get<{ count: number }>("/notifications/unread-count");
        this.unreadCount = res.data.count;
      } catch {
        // фоновая проверка - при ошибке оставляем прежнее значение
      }
    },
    async fetchPage(reset = false) {
      this.loading = true;
      try {
        const params: Record<string, unknown> = { limit: 20, lang: i18n.global.locale.value };
        if (!reset && this.nextCursor) params.cursor = this.nextCursor;
        const res = await api.get<FeedPage>("/notifications", { params });
        this.items = reset ? res.data.items : [...this.items, ...res.data.items];
        this.nextCursor = res.data.next_cursor;
        this.unreadCount = res.data.unread_count;
      } finally {
        this.loading = false;
      }
    },
    // Только личные уведомления; у объявлений своя отметка (открытие документа).
    async markRead(id: number) {
      const item = this.items.find((i) => i.type === "notification" && i.id === id);
      if (item && !item.read) {
        item.read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }
      await api.post(`/notifications/${id}/read`);
    },
    // read-all не снимает ожидающие юридические документы, поэтому итоговый
    // счётчик берём с сервера, а не обнуляем.
    async markAllRead() {
      for (const item of this.items) if (item.type === "notification") item.read = true;
      await api.post("/notifications/read-all");
      await this.fetchCount();
    },
    async accept(slug: string) {
      await api.post(`/announcements/${slug}/accept`);
      for (const item of this.items) {
        if (item.announcement?.slug === slug && item.announcement.legal) {
          item.announcement.legal.accepted = true;
          item.read = true;
        }
      }
      await this.fetchCount();
    },
    reset() {
      this.unreadCount = 0;
      this.items = [];
      this.nextCursor = null;
      this.loading = false;
    },
    // Вызывается один раз (App.vue). Событие без user_ids - рассылка всем.
    subscribe() {
      if (subscribed) return;
      subscribed = true;
      onServerEvent("notifications", (data) => {
        const auth = useAuthStore();
        if (!auth.isAuthenticated) return;
        const ids = (data as { user_ids?: number[] } | null)?.user_ids;
        if (!ids || auth.userId === null || ids.includes(auth.userId)) void this.fetchCount();
      });
    },
  },
});
